import { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  Minus,
  Edit,
  Trash2,
  Plus,
} from 'lucide-react';
import {
  EconomicEvent,
  Country,
  IMPORTANCE_LEVELS,
} from '../../constants/economicCalendarData';
import moment from 'moment';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../contexts/AuthContext';
import { economicCalendarAPI } from '../../services/api';
import toast from 'react-hot-toast';
import EventForm from './EventForm';
import DialogComponent from './Dialog';
import LoaderOverlay from './LoaderOverlay';
import ConfirmationModal from './ConfirmationModal';
import { getImportanceColor } from '../../utils/helpers';
import SourceCombobox from './SourceCombobox';
import DatePicker from './DatePicker';

const ALL_COUNTRIES_LABEL = 'All Countries';

const getCountryQueryValue = (country: string) => country.trim().toUpperCase();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const extractRawEvents = (response: unknown): unknown[] => {
  if (!isRecord(response) || !isRecord(response.data)) {
    return [];
  }

  if (Array.isArray(response.data.events)) {
    return response.data.events;
  }

  if (
    isRecord(response.data.data) &&
    Array.isArray(response.data.data.events)
  ) {
    return response.data.data.events;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  return [];
};

const EconomicCalendar = () => {
  const { user } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState('ALL');
  const [selectedImportance, setSelectedImportance] = useState('all');
  const [events, setEvents] = useState<EconomicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EconomicEvent | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteEvent, setDeleteEvent] = useState<EconomicEvent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterCountries, setFilterCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  const isAdmin = user?.role === 'super_admin';

  const countryFilterItems = useMemo(
    () => [
      ALL_COUNTRIES_LABEL,
      ...filterCountries.map(country => country.name),
    ],
    [filterCountries]
  );

  const selectedCountryLabel =
    selectedCountry === 'ALL' ? ALL_COUNTRIES_LABEL : selectedCountry;

  const selectedImportanceLabel =
    IMPORTANCE_LEVELS.find(level => level.value === selectedImportance)
      ?.label || '';

  const fetchFilterCountries = useCallback(async () => {
    try {
      setLoadingCountries(true);
      const response = await economicCalendarAPI.getCountries();
      const rawCountries = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.countries)
          ? response.data.countries
          : [];

      const formattedCountries = rawCountries
        .map((country: any) => ({
          name: country.name || country.countryName || '',
          phoneCode: country.phoneCode || '',
          currency: country.currency || '',
          timezone: Array.isArray(country.timezone)
            ? country.timezone
            : country.timezone
              ? [country.timezone]
              : [],
        }))
        .filter((country: Country) => country.name);

      setFilterCountries(formattedCountries);
    } catch (error) {
      toast.error('Failed to load country filter list');
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const mapApiEvent = (event: any): EconomicEvent => ({
    id: event.id ? String(event.id) : '',
    event_id: event.event_id ? String(event.event_id) : event.eventId || '',
    eventName: event.title || event.eventName || '',
    description: event.description || '',
    startDate: event.start_date || event.startDate || '',
    endDate: event.end_date || event.endDate || '',
    startTime: event.start_time || event.startTime || '',
    endTime: event.end_time || event.endTime || '',
    country: event.country || event.country_name || '',
    currency: event.currency || '',
    timezone: event.timezone || '',
    category: (event.category || 'other') as any,
    importance: (event.impact_level || event.importance || 'medium') as
      | 'low'
      | 'medium'
      | 'high',
    actualValue: event.actual_value
      ? String(event.actual_value).trim()
      : event.actualValue || '',
    forecastValue: event.forecast_value
      ? String(event.forecast_value).trim()
      : event.forecastValue || '',
    previousValue: event.previous_value
      ? String(event.previous_value).trim()
      : event.previousValue || '',
    sourceUrl: event.source_url || event.sourceUrl || '',
    createdAt: event.created_at || event.createdAt,
    updatedAt: event.updated_at || event.updatedAt,
  });

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: 1,
        limit: 20,
      };

      if (selectedCountry !== 'ALL') {
        params.country = getCountryQueryValue(selectedCountry);
      }

      if (selectedImportance !== 'all') {
        params.impact_level = selectedImportance;
      }

      if (startDateFilter) {
        params.start_date = startDateFilter;
      }

      if (endDateFilter) {
        params.end_date = endDateFilter;
      }

      const response = await economicCalendarAPI.getAll(params);
      const rawEvents = extractRawEvents(response);

      setEvents(rawEvents.map(mapApiEvent));
    } catch (error) {
      toast.error('Failed to load economic events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [endDateFilter, selectedCountry, selectedImportance, startDateFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    fetchFilterCountries();
  }, [fetchFilterCountries]);

  const getActualVsForecastIcon = (actual?: string, forecast?: string) => {
    if (!actual || !forecast) return null;

    const actualNum = parseFloat(actual.replace('%', ''));
    const forecastNum = parseFloat(forecast.replace('%', ''));

    if (isNaN(actualNum) || isNaN(forecastNum)) return null;

    if (actualNum > forecastNum) {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    } else if (actualNum < forecastNum) {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  const groupedEvents = useMemo(() => {
    const groups: { [key: string]: EconomicEvent[] } = {};
    events.forEach(event => {
      const date = moment(event.startDate).format('MMMM DD, YYYY');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });
    return groups;
  }, [events]);

  const isPastEvent = (event: EconomicEvent) => {
    const eventDate = moment(event.startDate);
    const now = moment();
    return eventDate.isBefore(now, 'day');
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (event: EconomicEvent) => {
    if (isPastEvent(event)) {
      toast.error('Cannot edit past events');
      return;
    }
    setEditingEvent(event);
    setIsDialogOpen(true);
  };

  const handleDelete = (event: EconomicEvent) => {
    if (isPastEvent(event)) {
      toast.error('Cannot delete past events');
      return;
    }

    setDeleteEvent(event);
  };

  const handleConfirmDelete = async () => {
    if (!deleteEvent) return;

    try {
      setIsDeleting(true);
      await economicCalendarAPI.delete(deleteEvent.id);
      toast.success('Event deleted successfully');
      setDeleteEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setDeleteEvent(null);
  };

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      if (editingEvent) {
        const result = await economicCalendarAPI.update(editingEvent.id, data);
        if (result?.success) {
          toast.success(result.message || 'Event updated successfully');
        } else {
          toast.error(result?.message || 'Failed to update event');
        }
      } else {
        const result = await economicCalendarAPI.create(data);
        if (result?.success) {
          toast.success(result.message || 'Event created successfully');
        } else {
          toast.error(result?.message || 'Failed to create event');
        }
      }
      setIsDialogOpen(false);
      fetchEvents();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (editingEvent ? 'Failed to update event' : 'Failed to create event');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingEvent(null);
  };

  const handleClearFilters = () => {
    setSelectedCountry('ALL');
    setSelectedImportance('all');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  const hasActiveFilters =
    selectedCountry !== 'ALL' ||
    selectedImportance !== 'all' ||
    Boolean(startDateFilter) ||
    Boolean(endDateFilter);

  return (
    <LoaderOverlay isLoading={loading} loadingText="Loading economic events...">
      <div className="space-y-6">
        <div className="sticky top-0 z-20 bg-background pt-4">
          <PageHeader
            title="Economic Calendar"
            description="Track important economic events and their impact on the forex market"
            actionLabel={isAdmin ? 'Add Event' : undefined}
            actionIcon={isAdmin ? Plus : undefined}
            onAction={isAdmin ? handleCreate : undefined}
          />
        </div>
        <Card>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 flex-1">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Country
                  </label>
                  <SourceCombobox
                    id="economic-country-filter"
                    items={countryFilterItems}
                    placeholder="Select country"
                    value={selectedCountryLabel}
                    onChange={value =>
                      setSelectedCountry(
                        value === ALL_COUNTRIES_LABEL ? 'ALL' : value
                      )
                    }
                    disabled={loadingCountries}
                    emptyText="No countries found."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Importance
                  </label>
                  <SourceCombobox
                    id="economic-importance-filter"
                    items={IMPORTANCE_LEVELS.map(level => level.label)}
                    placeholder="Select importance"
                    value={selectedImportanceLabel}
                    onChange={value => {
                      const level = IMPORTANCE_LEVELS.find(
                        option => option.label === value
                      );
                      if (level) {
                        setSelectedImportance(level.value);
                      }
                    }}
                    emptyText="No importance levels found."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Start Date
                  </label>
                  <DatePicker
                    id="economic-start-date-filter"
                    value={startDateFilter}
                    onChange={value => {
                      setStartDateFilter(value);
                      if (
                        endDateFilter &&
                        moment(endDateFilter).isBefore(moment(value), 'day')
                      ) {
                        setEndDateFilter('');
                      }
                    }}
                    placeholder="Pick a start date"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    End Date
                  </label>
                  <DatePicker
                    id="economic-end-date-filter"
                    value={endDateFilter}
                    onChange={setEndDateFilter}
                    placeholder="Pick an end date"
                    disabledBefore={
                      startDateFilter
                        ? moment(startDateFilter).startOf('day').toDate()
                        : undefined
                    }
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground mb-4">
              Showing {events.length} events
            </div>

            <div className="space-y-6">
              {Object.keys(groupedEvents).map((date: string) => {
                const events: EconomicEvent[] = groupedEvents[date] || [];
                return (
                  <div key={date}>
                    <h3 className="text-lg font-semibold mb-3 sticky top-0 bg-background py-2 border-b">
                      {date}
                    </h3>
                    <div className="space-y-3">
                      {events.map((event: EconomicEvent) => (
                        <Card
                          key={event.id}
                          className="hover:shadow-md transition-shadow"
                        >
                          <CardContent className="p-4">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium">
                                    {event.country}
                                  </span>
                                  <Badge
                                    className={getImportanceColor(
                                      event.importance
                                    )}
                                  >
                                    {event.importance.toUpperCase()}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground">
                                    {event.startTime}
                                  </span>
                                  {isPastEvent(event) && (
                                    <Badge
                                      variant="outline"
                                      className="text-muted-foreground"
                                    >
                                      Past
                                    </Badge>
                                  )}
                                </div>
                                <h4 className="font-semibold text-lg">
                                  {event.eventName}
                                </h4>
                              </div>

                              {isAdmin && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(event)}
                                    disabled={isPastEvent(event)}
                                    title={
                                      isPastEvent(event)
                                        ? 'Cannot edit past events'
                                        : 'Edit event'
                                    }
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(event)}
                                    disabled={isPastEvent(event)}
                                    title={
                                      isPastEvent(event)
                                        ? 'Cannot delete past events'
                                        : 'Delete event'
                                    }
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}

                              <div className="flex gap-4 text-sm">
                                <div className="text-center">
                                  <div className="text-muted-foreground text-xs mb-1">
                                    Actual
                                  </div>
                                  <div className="font-medium flex items-center gap-1">
                                    {event.actualValue || '-'}
                                    {getActualVsForecastIcon(
                                      event.actualValue,
                                      event.forecastValue
                                    )}
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-muted-foreground text-xs mb-1">
                                    Forecast
                                  </div>
                                  <div className="font-medium">
                                    {event.forecastValue || '-'}
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="text-muted-foreground text-xs mb-1">
                                    Previous
                                  </div>
                                  <div className="font-medium">
                                    {event.previousValue || '-'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {events.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {loading
                    ? 'Loading events...'
                    : 'No events found for the selected filters'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <DialogComponent
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onClose={handleDialogClose}
          title={editingEvent ? 'Edit Economic Event' : 'Create Economic Event'}
          description={
            editingEvent
              ? 'Update the economic event details'
              : 'Fill in the details to create a new economic event'
          }
          maxWidth="2xl"
          onSubmit={() => {
            const form = document.querySelector('form');
            if (form) form.requestSubmit();
          }}
          submitText={editingEvent ? 'Update Event' : 'Create Event'}
          onCancel={handleDialogClose}
          cancelText="Cancel"
          disabled={isSubmitting}
        >
          <LoaderOverlay
            isLoading={isSubmitting}
            loadingText={
              editingEvent ? 'Updating economic event...' : 'Creating event...'
            }
          >
            <EventForm
              onSubmit={handleFormSubmit}
              initialData={editingEvent || undefined}
            />
          </LoaderOverlay>
        </DialogComponent>

        <ConfirmationModal
          isOpen={!!deleteEvent}
          title="Delete Economic Event"
          message={`Are you sure you want to delete "${deleteEvent?.event_id}"?`}
          confirmText="Delete Event"
          cancelText="Cancel"
          type="danger"
          loading={isDeleting}
          loadingText="Deleting event..."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>
    </LoaderOverlay>
  );
};

export default EconomicCalendar;
